/**
 * Entra (Microsoft) SSO Service
 *
 * Implements the OAuth 2.0 authorization-code + PKCE flow against the Cognito
 * hosted UI, which brokers to Entra. The hosted UI's own login form is never
 * shown: `identity_provider=AzureAD` sends the browser straight to Microsoft.
 *
 * Why this is hand-rolled rather than done with Amplify:
 *
 *   The whole app authenticates through `amazon-cognito-identity-js` — session
 *   restore, silent refresh in the API layer, sign-out, the admin portal. Adding
 *   Amplify would mean two libraries owning the same session. Instead the tokens
 *   returned by the code exchange are handed to `CognitoUser.setSignInUserSession`,
 *   which stores them under the keys the rest of the app already reads, so an SSO
 *   session is indistinguishable from a password session from that point on
 *   (including refresh, because the hosted UI issues a refresh token for the same
 *   app client).
 *
 * The app client is public (no secret), so PKCE is what binds the authorization
 * code to this browser.
 */

import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import {
  getCognitoClientId,
  getCognitoDomain,
  getCognitoUserPoolId,
  getSsoRedirectUri,
} from '../config';

/** Provider name of the Entra IdP on the user pool; must match template.yaml. */
const IDENTITY_PROVIDER = 'AzureAD';

/**
 * PKCE verifier and CSRF state live in sessionStorage: they must survive the
 * redirect to Microsoft and back, but must not outlive the tab.
 */
const PKCE_VERIFIER_KEY = 'sso_pkce_verifier';
const STATE_KEY = 'sso_state';
const RETURN_TO_KEY = 'sso_return_to';

export class SSOError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SSOError';
  }
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Start Microsoft sign-in by redirecting to the Cognito hosted UI.
 *
 * Never returns on success — the browser navigates away.
 *
 * @param returnTo - In-app path to land on once sign-in completes.
 */
export async function beginSSOLogin(returnTo = '/'): Promise<void> {
  const verifier = randomUrlSafeString(32);
  const state = randomUrlSafeString(16);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: getCognitoClientId(),
    redirect_uri: getSsoRedirectUri(),
    // `aws.cognito.signin.user.admin` is required for the SDK's GetUser call
    // (CognitoUser.getUserAttributes). A hosted-UI access token carries only the
    // scopes requested HERE and allowed on the app client, so dropping it makes
    // every GetUser return 400 on a federated session (native USER_PASSWORD_AUTH
    // tokens include it implicitly, which is why password sign-in is unaffected).
    scope: 'openid email profile aws.cognito.signin.user.admin',
    identity_provider: IDENTITY_PROVIDER,
    state,
    code_challenge: await sha256Challenge(verifier),
    code_challenge_method: 'S256',
  });

  window.location.assign(`${getCognitoDomain()}/oauth2/authorize?${params.toString()}`);
}

/**
 * Discard the single-use PKCE verifier and CSRF state.
 *
 * Deliberately leaves the return-to destination alone: that is consumed
 * separately by consumeReturnTo() AFTER the code exchange, so clearing it here
 * would drop the page the user was heading to on every sign-in. A destination
 * left behind by an abandoned attempt is harmless — the next beginSSOLogin()
 * overwrites it and consumeReturnTo() removes it.
 */
export function clearSSOFlowState(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}

/** Where the user asked to go before being sent to Microsoft. */
export function consumeReturnTo(): string {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo || '/';
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Exchange the authorization code for tokens and install them as the app's
 * Cognito session.
 *
 * @returns the signed-in CognitoUser, ready for `getSession()` like any other.
 */
export async function completeSSOLogin(code: string, state: string | null): Promise<CognitoUser> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  // Both are single-use: clear them before any awaits so a replayed callback
  // cannot reuse them, whether the exchange below succeeds or fails.
  clearSSOFlowState();

  if (!verifier || !expectedState) {
    throw new SSOError('Sign-in session expired. Please start sign in again.');
  }
  if (!state || state !== expectedState) {
    throw new SSOError('Sign-in could not be verified. Please start sign in again.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: getCognitoClientId(),
    code,
    redirect_uri: getSsoRedirectUri(),
    code_verifier: verifier,
  });

  const response = await fetch(`${getCognitoDomain()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    // Cognito returns {error, error_description}; neither is safe to show as-is
    // (it can contain the raw redirect_uri), so log it and show a stable message.
    const detail = await response.text().catch(() => '');
    console.error('SSO token exchange failed:', response.status, detail);
    throw new SSOError('Microsoft sign-in could not be completed. Please try again.');
  }

  const tokens = (await response.json()) as TokenResponse;

  if (!tokens.id_token || !tokens.access_token || !tokens.refresh_token) {
    throw new SSOError('Microsoft sign-in returned an incomplete session. Please try again.');
  }

  return installSession(tokens);
}

/**
 * Store hosted-UI tokens the way `amazon-cognito-identity-js` stores its own, so
 * `userPool.getCurrentUser()` finds the session and can refresh it.
 */
function installSession(tokens: TokenResponse): CognitoUser {
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });

  const userPool = new CognitoUserPool({
    UserPoolId: getCognitoUserPoolId(),
    ClientId: getCognitoClientId(),
  });

  // The library keys its storage by username, and expects the value Cognito puts
  // in the access token's `username` claim. For a linked federated sign-in that
  // is the native profile's username, which is exactly what a password sign-in
  // would have used.
  const username = session.getAccessToken().payload['username'];
  if (!username) {
    throw new SSOError('Microsoft sign-in returned a session without a username.');
  }

  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.setSignInUserSession(session);
  return cognitoUser;
}

/**
 * End the Cognito hosted-UI session as well as the local one.
 *
 * Without this the hosted UI keeps its own session cookie, and the next "Sign in
 * with Microsoft" silently signs the previous person back in — which looks like
 * sign-out is broken, and is a real problem on shared machines.
 *
 * Never returns — the browser navigates to Cognito and is redirected back to the
 * sign-in page.
 */
export function redirectToSSOLogout(): void {
  const params = new URLSearchParams({
    client_id: getCognitoClientId(),
    logout_uri: `${window.location.origin}/signin`,
  });
  window.location.assign(`${getCognitoDomain()}/logout?${params.toString()}`);
}

/**
 * Whether the current session came from an identity provider rather than from a
 * password. Cognito puts the federated identities on the ID token.
 */
export function isFederatedSession(idToken: string | undefined): boolean {
  if (!idToken) return false;
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
    return Boolean(payload.identities);
  } catch {
    return false;
  }
}
